import type { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import styled from '@emotion/styled';
import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';
import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import { selectText } from '@hotjar/webapp/utils/select-text';
import { Modal } from '@hotjar/hotsauce';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ShortLinkDropdown } from '../../ShortLink';
import { useShortUrl } from '../../ShortLink/use-short-url';

type HighlightShareModalProps = {
  siteId: string;
  isOpen: boolean;
  highlight: {
    id: string;
    fullItemUrl: string;
  };
  onClose: () => void;
  uiPosition?: UIPosition;
};

const StyledModal = styled(Modal)`
  width: 326px;
  height: 128px;
  min-height: 128px;
  margin: 30% auto;
`;

export const HighlightShareModal = ({
  siteId,
  highlight,
  isOpen,
  onClose,
  uiPosition,
}: HighlightShareModalProps): ReactJSXElement => {
  const outputRef = useRef<HTMLOutputElement | null>(null);
  const shortUrl = useShortUrl(siteId, highlight.fullItemUrl);
  const { t } = useTranslation();

  const selectAndCopy = useCallback(() => {
    if (outputRef.current !== null) {
      selectText(outputRef.current);
    }

    if (shortUrl.status !== 'success') {
      return;
    }

    if (siteId) {
      sendHighlightEvent(
        siteId,
        { highlightId: highlight.id, eventType: HighlightsEventType.HIGHLIGHT_SHARED },
        { uiPosition },
      );
    }
  }, [shortUrl, siteId, highlight.id, uiPosition]);

  return (
    <StyledModal
      appElementSelector="#root"
      contentLabel={t('common:actions.share')}
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
    >
      <Modal.Body>
        <ShortLinkDropdown onCopy={selectAndCopy} ref={outputRef} />
      </Modal.Body>
    </StyledModal>
  );
};
